import { type AccessPlace } from '@church/domain'
import {
  canSeeServicesPerson,
  isLevelOf,
  peopleReach,
  placeLevel,
  type GrantScope,
  type PeopleReach,
  type PlaceLevel,
  type PlanningLevel,
  type PlanningSource,
} from '@church/permissions'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApi } from '../api-context.tsx'
import { useAccess, useServiceAccess } from '../access/useAccess.ts'
import { usePersonGrants, type GrantRow } from '../access/useAccessGrants.ts'
import { placeKey } from '../access/useGrantForm.ts'
import { useDebounced } from '../people/usePeople.ts'
import { servicesKeys } from './useServices.ts'

/**
 * ForServices' People (docs/14-TEAMS-PEOPLE-TEMPLATES.md, "People
 * (ForServices)"): the People page, a person's profile with its Permissions
 * folder tree, the serving details an Editor changes there, and whether a
 * name opens a profile at all. Cosmetic where it decides: the server
 * refuses what the screens hide.
 */

export const servicesPeopleKeys = {
  all: () => [...servicesKeys.all(), 'people'] as const,
  list: (filters: { q: string; mine: boolean }) =>
    [...servicesPeopleKeys.all(), 'list', filters] as const,
  profile: (id: string) => [...servicesPeopleKeys.all(), 'profile', id] as const,
}

/**
 * Whom their People page lists (`peopleReach`): none for a Scheduled
 * viewer (no page, no tab), their own teams' people for a Viewer or
 * Scheduler (leading a team included), everyone for an Editor.
 */
export function usePeopleReach(): PeopleReach {
  const access = useAccess()
  return access.servicesLocked ? 'none' : peopleReach(access.access)
}

/**
 * The People page: A–Z, a page at a time as the list scrolls (virtualized
 * on screen), narrowed by a name and — for an Editor, who lists everyone —
 * to the teams they are on or lead ("On my teams").
 */
export function useServicesPeopleList(filters: { q: string; mine: boolean }) {
  const api = useApi()
  const reach = usePeopleReach()
  const q = useDebounced(filters.q.trim(), 250)
  const mine = reach === 'everyone' && filters.mine
  const query = useInfiniteQuery({
    queryKey: servicesPeopleKeys.list({ q, mine }),
    enabled: reach !== 'none',
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.services.people.get({
        query: {
          limit: 100,
          ...(pageParam ? { cursor: pageParam } : {}),
          ...(q ? { q } : {}),
          ...(mine ? { mine: true } : {}),
        },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
  })
  return {
    reach,
    people: query.data?.pages.flatMap((page) => page.items) ?? [],
    /** Every page is here, so the count is the whole count. */
    complete: !query.hasNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    isLoading: query.isPending && reach !== 'none',
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    /** The CSV of this list (an Editor's), as a link to download. */
    exportHref:
      reach === 'everyone' ? `/api/v1/services/people.csv${mine ? '?mine=true' : ''}` : null,
  }
}

export type ServicesPersonRow = ReturnType<typeof useServicesPeopleList>['people'][number]

/** A person's ForServices profile; 404 (an error) when they may not see them. */
export function useServicesProfile(personId: string) {
  const api = useApi()
  return useQuery({
    queryKey: servicesPeopleKeys.profile(personId),
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.people({ id: personId }).profile.get()
      if (error) throw error
      return data
    },
  })
}

export type ServicesProfile = NonNullable<ReturnType<typeof useServicesProfile>['data']>

/**
 * What an Editor (or the person, or their Scheduler) changes from a
 * profile: their positions, blockouts and monthly limit. Each answer is the
 * profile again; rosters elsewhere are refreshed.
 */
export function useServicesProfileEditor(personId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const put = (profile: ServicesProfile) => {
    queryClient.setQueryData(servicesPeopleKeys.profile(personId), profile)
    void queryClient.invalidateQueries({ queryKey: servicesKeys.teams() })
    void queryClient.invalidateQueries({ queryKey: [...servicesPeopleKeys.all(), 'list'] })
  }
  const person = api.api.v1.services.people({ id: personId })
  const setPosition = useMutation({
    mutationFn: async ({ positionId, on }: { positionId: string; on: boolean }) => {
      const { data, error } = on
        ? await person.positions({ positionId }).put()
        : await person.positions({ positionId }).delete()
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const setMaxPerMonth = useMutation({
    mutationFn: async (maxPerMonth: number | null) => {
      const { data, error } = await person.preferences.patch({ maxPerMonth })
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const addBlockout = useMutation({
    mutationFn: async (body: { startsOn: string; endsOn: string; reason?: string | null }) => {
      const { error } = await api.api.v1.services.blockouts.post({ ...body, personId })
      if (error) throw error
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: servicesPeopleKeys.profile(personId) }),
  })
  const removeBlockout = useMutation({
    mutationFn: async (blockoutId: string) => {
      const { data, error } = await person.blockouts({ blockoutId }).delete()
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  return { setPosition, setMaxPerMonth, addBlockout, removeBlockout }
}

/**
 * Whether a name opens that person's profile (`canSeeServicesPerson`): as
 * the server decides it, from what the screen knows. `serves` says the
 * person is shown serving where the reader sees (a roster, a plan's people,
 * the schedule, the People list); a name with no such context (who last
 * changed a plan) opens only for a directory reader or an Editor.
 */
export function useProfileLinks() {
  const access = useAccess()
  const reach = usePeopleReach()
  return {
    canOpen: (serves: boolean) =>
      access.inServices &&
      canSeeServicesPerson({
        directory: access.allows('people:read'),
        editorSomewhere: reach === 'everyone',
        servesWhereSeen: serves,
      }),
  }
}

// ---- the Permissions folder tree ----------------------------------------------------------

/** One row of a person's Permissions tree: a place, and their level there. */
export interface PermissionTreeRow {
  key: string
  scope: GrantScope
  name: string
  kind: AccessPlace['kind']
  depth: number
  /** A folder with places inside it: it opens and closes. */
  hasChildren: boolean
  open: boolean
  level: PlaceLevel
  /** The teams they lead that serve here: "Scheduler · leads Worship Band". */
  leadsTeams: string[]
  /** The grant they hold right here, when the reader may change it. */
  grant: GrantRow | null
  /** The reader gives Planning & people access here (`canGrant`): the row opens the panel. */
  givable: boolean
}

const scopeOf = (place: AccessPlace): GrantScope =>
  place.kind === 'org' || place.id === null ? { kind: 'org' } : { kind: place.kind, id: place.id }

const sameScope = (row: { scopeType: string; scopeId: string | null }, scope: GrantScope) =>
  row.scopeType === scope.kind &&
  (scope.kind === 'org' ? row.scopeId === null : row.scopeId === scope.id)

/**
 * A person's Planning & people levels on the places the reader sees, as an
 * expandable tree (docs/14): the church, then each folder with its folders
 * and service types inside it. Each row says what is set there and what it
 * inherits from above (`placeLevel`); the reader opens the grant panel on
 * a row where they give access. Folders start closed, except those on the
 * way to a level set below them.
 */
export function usePermissionTree(profile: ServicesProfile | undefined) {
  const access = useServiceAccess()
  const login = profile?.access ?? null
  // By person: their login's grants, or — with no login yet — what waits for them.
  const grants = usePersonGrants(login !== null && profile ? profile.id : null)
  // Their levels by source. Grants the reader may change come from their
  // grant list (`useUserGrants`), so a change shows at once; the rest — their
  // role, the default, grants beyond the reader's reach — from the profile.
  const toSource = (row: {
    level: string
    scopeType: string
    scopeId: string | null
    from: string
  }): PlanningSource[] => {
    if (!isLevelOf('planning', row.level)) return []
    const scope: GrantScope =
      row.scopeType === 'org' || row.scopeId === null
        ? { kind: 'org' }
        : { kind: row.scopeType === 'folder' ? 'folder' : 'service_type', id: row.scopeId }
    const from = row.from === 'role' || row.from === 'default' ? row.from : 'grant'
    return [{ level: row.level as PlanningLevel, scope, from }]
  }
  const changeable = (row: { level: string; scopeType: string; scopeId: string | null }) =>
    access.canChange({ app: 'services', module: 'planning', ...row })
  const live = grants.data !== undefined
  const sources: PlanningSource[] = [
    ...(login?.sources ?? [])
      .filter((row) => row.from !== 'grant' || !live || !changeable(row))
      .flatMap(toSource),
    ...(grants.data ?? [])
      .filter((row) => row.module === 'planning')
      .flatMap((row) => toSource({ ...row, from: 'grant' })),
  ]
  const led = (profile?.teams ?? []).filter((team) => team.leads)
  const person = {
    sources,
    churchAdmin: login?.churchAdmin ?? false,
    leading: led.map((team) => ({ teamId: team.id, serviceTypeIds: team.serviceTypeIds })),
  }

  // The places in tree order; each one's folder ancestors, to know what is shown.
  const places = access.places
  const ancestors: string[][] = []
  const stack: { depth: number; id: string }[] = []
  for (const place of places) {
    while (stack.length > 0 && (stack[stack.length - 1]?.depth ?? 0) >= place.depth) stack.pop()
    ancestors.push(stack.map((entry) => entry.id))
    if (place.kind === 'folder' && place.id) stack.push({ depth: place.depth, id: place.id })
  }
  // Open from the start: the folders above any level set for them below.
  const setBelow = new Set<string>()
  places.forEach((place, index) => {
    const key = placeKey(scopeOf(place))
    const held = sources.some((source) => source.from === 'grant' && placeKey(source.scope) === key)
    if (held) for (const id of ancestors[index] ?? []) setBelow.add(id)
  })
  // Opened or closed by hand; otherwise open exactly when a level is set below.
  const [chosen, setChosen] = useState<ReadonlyMap<string, boolean>>(new Map())
  const isOpen = (id: string) => chosen.get(id) ?? setBelow.has(id)

  const rows: PermissionTreeRow[] = []
  places.forEach((place, index) => {
    if (!(ancestors[index] ?? []).every(isOpen)) return
    const scope = scopeOf(place)
    const next = places[index + 1]
    rows.push({
      key: placeKey(scope),
      scope,
      name: place.name,
      kind: place.kind,
      depth: place.depth,
      hasChildren: place.kind === 'folder' && next !== undefined && next.depth > place.depth,
      open: place.kind === 'folder' && place.id !== null && isOpen(place.id),
      level: placeLevel(person, scope, access.tree),
      // Leading reaches a team's own service types only (no church-wide teams, docs/14).
      leadsTeams: led
        .filter((team) => scope.kind === 'service_type' && team.serviceTypeIds.includes(scope.id))
        .map((team) => team.name),
      grant:
        (grants.data ?? []).find((row) => row.module === 'planning' && sameScope(row, scope)) ??
        null,
      givable: access.grantable('planning', scope).length > 0,
    })
  })

  return {
    /** Shown only to whoever gives access, with or without a login of theirs. */
    shown: login !== null,
    ready: access.ready && (login === null || !grants.isPending),
    error: grants.error,
    userId: login?.userId ?? null,
    /** The person whose levels these are (a grant panel gives to them). */
    personId: profile?.id ?? null,
    /** They have a login here; without one, their levels apply when they sign in. */
    signedIn: login?.signedIn ?? false,
    rows,
    /** "Scheduler · leads Worship Band": the levels leading gives them, one per team. */
    leading: led.map((team) => team.name),
    toggle: (folderId: string) =>
      setChosen((current) => new Map(current).set(folderId, !isOpen(folderId))),
    placeLabel: access.placeLabel,
  }
}
